/**
 * useInvestigateStream — manages the SSE stream lifecycle for /investigate.
 *
 * Two modes the hook supports:
 *   - `live`: opens POST /api/investigate/. Used by the InvestigateLivePage
 *     after the user submits the prompt form.
 *   - `catchup`: opens GET /api/investigations/{id}/stream. Used by the
 *     detail page on running investigations (and as a manual replay on
 *     done/failed ones — the endpoint replays persisted events either way).
 *
 * State machine:
 *   idle      — initial; nothing in flight
 *   running   — stream is open, anchor received, events flowing
 *   done      — `done` event received with status='done'
 *   failed    — pre-stream HTTP error OR `done` event with status='failed'
 *
 * The hook owns:
 *   - the live (or replayed) anchor
 *   - the timeline entries (one per tool_call, mutated by tool_result —
 *     keyed on call_id so catchup-replay rows and live rows merge cleanly)
 *   - the streaming narrative text (concatenated from narrative_chunk events)
 *   - the final cost/tokens/rounds reported by the `done` event
 *
 * Returns a `start(req)` function that opens the stream and a `cancel()`
 * teardown that aborts the in-flight fetch (used by the page on unmount).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { investigate } from '@/helpers/ApiHelper';
import type { InvestigateStreamRequest } from '@/helpers/ApiHelper';
import {
  answerToString,
  type ClarificationAnswer,
} from '@/components/ClarificationPanel';
import type { TimelineEntry } from '@/components/InvestigationTimeline';
import type {
  ClarificationRequiredResponse,
  InvestigateRequest,
  InvestigationAnchor,
  MemoryEpisode,
  SSEEvent,
  SystemSnapshot,
} from '@/types/investigation';

/**
 * Hard cap on how many clarification rounds we'll ask the user before
 * surrendering. Each round costs one Haiku call (~$0.0002) and adds visible
 * friction, so we cap at 3 to keep the experience snappy. After the cap,
 * the panel renders "final round" copy and (if the heuristic still fires
 * after that round) the FE auto-injects skip_clarification=true so the
 * investigation actually runs.
 */
export const MAX_CLARIFICATION_ROUNDS = 3;

export type StreamStatus =
  | 'idle'
  | 'running'
  | 'done'
  | 'failed'
  // Pre-flight clarification gate (post-Sprint-12). Sits between the
  // user's POST and the SSE stream; the FE renders the inline questions
  // panel and waits for the user to submit answers or skip.
  | 'awaiting_clarification';

export interface InvestigateStreamState {
  status: StreamStatus;
  investigationId: string | null;
  anchor: InvestigationAnchor | null;
  // Phase C/E — populated by the synthetic `system_snapshot` SSE event,
  // emitted exactly once between `anchor` and the first `tool_call`.
  // Null until the snapshot lands (or always null on legacy investigations
  // captured before Phase C went live). Idempotent under catchup replay.
  systemSnapshot: SystemSnapshot | null;
  // Sprint 12 Phase B — populated by the synthetic `memory_context` SSE
  // event, emitted exactly once between `system_snapshot` and the first
  // `tool_call`. Empty array means the corpus had no matching episodes
  // (or the retrieval errored — the FE doesn't distinguish; either way
  // we render no chips). Null until the event lands.
  memoryEpisodes: MemoryEpisode[] | null;
  timeline: TimelineEntry[];
  narrative: string;
  costUsd: number;
  tokensUsed: number;
  rounds: number;
  error: { status?: number; message: string } | null;
  // Pre-flight clarification gate. Populated by the 202 response from
  // POST /api/investigate/; cleared on submitClarification/editPrompt.
  clarification: ClarificationRequiredResponse | null;
  // Iteration round, 1-based. Increments every time the backend returns
  // another 202 in the same chain. Resets when start() is called fresh.
  clarificationRound: number;
  // Accumulated texts of every question asked in this chain, forwarded
  // to the backend on each re-POST so the Haiku assistant can avoid
  // repeating itself.
  askedClarifications: string[];
}

export interface UseInvestigateStream {
  state: InvestigateStreamState;
  start: (request: InvestigateStreamRequest) => void;
  cancel: () => void;
  /**
   * Submit answers from the clarification panel. ``mode='augment'``
   * appends the Q/A pairs to the original prompt and re-POSTs.
   * ``mode='skip'`` re-POSTs the original prompt with skip_clarification=true.
   * No-op when state.clarification is null.
   */
  submitClarification: (
    answers: Record<number, ClarificationAnswer>,
    mode: 'augment' | 'skip',
  ) => void;
  /**
   * Drop the clarification panel and return to ``idle`` so the page can
   * re-render the prompt textarea. Returns the original prompt + a
   * structured draft of the Q/A pairs so the caller can restore them
   * in the textarea if it wants to preserve typing work.
   */
  editPrompt: (answers: Record<number, ClarificationAnswer>) => {
    originalPrompt: string;
    draft: string;
  } | null;
}

const initialState: InvestigateStreamState = {
  status: 'idle',
  investigationId: null,
  anchor: null,
  systemSnapshot: null,
  memoryEpisodes: null,
  timeline: [],
  narrative: '',
  costUsd: 0,
  tokensUsed: 0,
  rounds: 0,
  error: null,
  clarification: null,
  clarificationRound: 0,
  askedClarifications: [],
};


/**
 * Build the augmented prompt the FE re-POSTs after the user answers
 * clarifying questions. Format is intentionally human-readable so it lives
 * usefully in ``investigation.prompt`` after the row is persisted (debugging,
 * audit, reflection critic). Unanswered questions are skipped so a partial
 * submit still narrows the anchor.
 */
function buildAugmentedPrompt(
  originalPrompt: string,
  questions: { text: string }[],
  answers: Record<number, ClarificationAnswer>,
): string {
  const lines: string[] = [];
  questions.forEach((q, idx) => {
    const value = answerToString(answers[idx]);
    if (!value) return;
    lines.push(`- Q: ${q.text}\n  A: ${value}`);
  });
  if (lines.length === 0) {
    return originalPrompt;
  }
  return `${originalPrompt}\n\nAdditional context:\n${lines.join('\n')}`;
}

export function useInvestigateStream(): UseInvestigateStream {
  const [state, setState] = useState<InvestigateStreamState>(initialState);
  const teardownRef = useRef<(() => void) | null>(null);
  // Cache the last `live` request body so submitClarification can re-POST
  // with the augmented prompt (or skip flag) without the caller having to
  // re-thread the body through React state.
  const lastLiveBodyRef = useRef<InvestigateRequest | null>(null);

  const cancel = useCallback(() => {
    if (teardownRef.current) {
      teardownRef.current();
      teardownRef.current = null;
    }
  }, []);

  // Tear down the stream on unmount.
  useEffect(() => () => cancel(), [cancel]);

  const handleEvent = useCallback((evt: SSEEvent) => {
    setState((prev) => {
      switch (evt.event) {
        case 'anchor': {
          const data = evt.data;
          return {
            ...prev,
            status: 'running',
            investigationId: data.investigation_id,
            anchor: {
              from_ts: data.from_ts,
              to_ts: data.to_ts,
              services: data.services,
              symptoms: data.symptoms,
              raw_prompt: data.raw_prompt,
            },
          };
        }
        case 'system_snapshot': {
          // Idempotent: catchup replay can re-emit the snapshot the page
          // already received via the live stream. Either way the wire
          // payload is the persisted JSONB, so a straight set is correct.
          return { ...prev, systemSnapshot: evt.data.snapshot };
        }
        case 'memory_context': {
          // Sprint 12 Phase B — episodic-memory chip metadata. Idempotent
          // under catchup replay (the runner persists the bundle via
          // broker.publish; replay re-emits the same payload). Empty list
          // is meaningful: it tells the panel "clear any stale chips".
          return { ...prev, memoryEpisodes: evt.data.episodes };
        }
        case 'system_snapshot_insights': {
          // Phase G — emitted right after `system_snapshot` (when the
          // insights pass returns at least one item) and replayed from
          // JSONB on catchup. Idempotent: we replace insights wholesale
          // every time the event lands; the parent_snapshot reuse path
          // on E1L follow-ups reuses the parent's list verbatim, so
          // identical event content under catchup is a no-op set.
          //
          // If the snapshot hasn't arrived yet (e.g. an out-of-order
          // race between the broker publish and the FE state update),
          // we hold the insights in a synthetic placeholder so the next
          // `system_snapshot` event knows to merge them in.
          if (prev.systemSnapshot === null) {
            return prev;
          }
          return {
            ...prev,
            systemSnapshot: {
              ...prev.systemSnapshot,
              insights: evt.data.insights,
            },
          };
        }
        case 'tool_call': {
          const data = evt.data;
          // Idempotent: catchup replay can emit the same call_id we
          // already have in the timeline. Replace if it's there, append
          // otherwise.
          const existing = prev.timeline.findIndex(
            (row) => row.call_id === data.call_id,
          );
          const newRow: TimelineEntry = {
            call_id: data.call_id,
            round: data.round,
            tool: data.tool,
            args: data.args,
            result_preview: null,
            latency_ms: null,
            error: null,
          };
          const next =
            existing === -1
              ? [...prev.timeline, newRow]
              : prev.timeline.map((row, i) => (i === existing ? newRow : row));
          return { ...prev, timeline: next };
        }
        case 'tool_result': {
          const data = evt.data;
          return {
            ...prev,
            timeline: prev.timeline.map((row) =>
              row.call_id === data.call_id
                ? {
                    ...row,
                    result_preview: data.result_preview,
                    latency_ms: data.latency_ms,
                    error: data.error,
                  }
                : row,
            ),
          };
        }
        case 'narrative_chunk': {
          return {
            ...prev,
            narrative: prev.narrative + evt.data.text,
          };
        }
        case 'done': {
          const data = evt.data;
          return {
            ...prev,
            status: data.status,
            costUsd: data.cost_usd,
            tokensUsed: data.tokens_used,
            rounds: data.rounds,
            error:
              data.status === 'failed'
                ? { message: data.error ?? 'Investigation failed' }
                : null,
          };
        }
        default:
          return prev;
      }
    });
  }, []);

  /**
   * Internal: open the SSE / 202 stream. Two callers:
   *   - ``start()`` exposed below resets clarificationRound + askedClarifications
   *     before opening the very first request in a chain.
   *   - ``submitClarification(augment)`` keeps the chain alive across rounds
   *     (asked-questions accumulator continues, round increments).
   */
  const openStream = useCallback(
    (
      request: InvestigateStreamRequest,
      opts: { preserveChain?: boolean } = {},
    ) => {
      const preserveChain = opts.preserveChain === true;
      // Tear down any in-flight stream before opening a new one.
      cancel();
      // Cache the live body so the clarification flow can re-POST with the
      // augmented prompt. ``catchup`` mode never hits the gate, so cache
      // gets cleared in that branch.
      lastLiveBodyRef.current =
        request.mode === 'live' ? request.body : null;

      setState((prev) => ({
        ...initialState,
        status: 'running',
        // Preserve chain bookkeeping so the next 202 (if any) knows what
        // round we're on + which questions have already been asked.
        clarificationRound: preserveChain ? prev.clarificationRound : 0,
        askedClarifications: preserveChain ? prev.askedClarifications : [],
      }));

      teardownRef.current = investigate.stream(request, {
        onEvent: handleEvent,
        onHttpError: ({ status, detail }) => {
          setState((prev) => ({
            ...prev,
            status: 'failed',
            error: { status, message: detail },
          }));
        },
        onStreamError: (err) => {
          setState((prev) => ({
            ...prev,
            status: 'failed',
            error: { message: err.message },
          }));
        },
        onClarificationRequired: (payload) => {
          setState((prev) => {
            const nextRound = prev.clarificationRound + 1;
            const merged = [
              ...prev.askedClarifications,
              ...payload.questions.map((q) => q.text),
            ];
            return {
              ...prev,
              status: 'awaiting_clarification',
              clarification: payload,
              clarificationRound: nextRound,
              askedClarifications: merged,
            };
          });
        },
        onComplete: () => {
          // If we never got a `done` event but the stream closed, mark failed.
          // Exception: the clarification gate path closes the underlying
          // fetch right after the 202 payload arrives; that's the success
          // path for the gate, not a stream failure.
          setState((prev) =>
            prev.status === 'running'
              ? {
                  ...prev,
                  status: 'failed',
                  error: prev.error ?? { message: 'Stream ended without a done event' },
                }
              : prev,
          );
        },
      });
    },
    [cancel, handleEvent],
  );

  const start = useCallback(
    (request: InvestigateStreamRequest) => {
      openStream(request, { preserveChain: false });
    },
    [openStream],
  );

  const submitClarification = useCallback(
    (
      answers: Record<number, ClarificationAnswer>,
      mode: 'augment' | 'skip',
    ) => {
      const lastBody = lastLiveBodyRef.current;
      const clarification = state.clarification;
      if (!lastBody || !clarification) return;

      // If we've hit the cap and the user is still hitting "submit", force
      // skip_clarification so a third 202 can't appear. The panel header
      // tells the user this is the final round, and the augmented prompt
      // still carries their answers — but the gate is closed.
      const atCap = state.clarificationRound >= MAX_CLARIFICATION_ROUNDS;
      const effectiveMode: 'augment' | 'skip' = atCap ? 'skip' : mode;
      const augmentedPrompt =
        mode === 'augment'
          ? buildAugmentedPrompt(
              lastBody.prompt,
              clarification.questions,
              answers,
            )
          : lastBody.prompt;

      const newBody: InvestigateRequest = {
        ...lastBody,
        prompt: effectiveMode === 'augment' ? augmentedPrompt : augmentedPrompt,
        skip_clarification: effectiveMode === 'skip',
        asked_clarifications: state.askedClarifications,
      };

      openStream({ mode: 'live', body: newBody }, { preserveChain: true });
    },
    [openStream, state.askedClarifications, state.clarification, state.clarificationRound],
  );

  const editPrompt = useCallback(
    (answers: Record<number, ClarificationAnswer>) => {
      const lastBody = lastLiveBodyRef.current;
      const clarification = state.clarification;
      if (!lastBody || !clarification) return null;

      const draft = buildAugmentedPrompt(
        lastBody.prompt,
        clarification.questions,
        answers,
      );
      // Reset the hook state so the page re-renders the textarea path.
      setState(initialState);
      lastLiveBodyRef.current = null;
      return { originalPrompt: lastBody.prompt, draft };
    },
    [state.clarification],
  );

  return { state, start, cancel, submitClarification, editPrompt };
}
